import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  CheckCircle,
  XCircle,
  Calendar,
  MapPin,
  User,
  AlertTriangle,
  Printer
} from 'lucide-react';

const ChecklistDetailPage = () => {
  const { checklistId } = useParams();
  const navigate = useNavigate();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChecklistDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklistId]);

  const fetchChecklistDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Try to get the checklist directly by ID first
      const directResponse = await fetch(`/api/admin/checklist/${checklistId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (directResponse.ok) {
        const directData = await directResponse.json();
        console.log('Direct API response:', directData);
        if (directData.success && directData.checklist) {
          console.log('Checklist data:', directData.checklist);
          setChecklist(directData.checklist);
          setError(null);
          return;
        }
      }

      // Fallback: Search through completed checklists
      const completedResponse = await fetch('/api/admin/completed-checklists', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (completedResponse.ok) {
        const completedData = await completedResponse.json();
        
        // Search through the organized data to find our checklist
        let foundChecklist = null;
        
        if (completedData.data) {
          for (const monthData of completedData.data) {
            for (const districtData of monthData.districts) {
              const checklist = districtData.checklists.find(c => c._id === checklistId);
              if (checklist) {
                foundChecklist = checklist;
                break;
              }
            }
            if (foundChecklist) break;
          }
        }

        if (foundChecklist) {
          setChecklist(foundChecklist);
          setError(null);
        } else {
          setError('Checklist not found');
        }
      } else {
        setError('Failed to fetch checklist details');
      }
    } catch (err) {
      console.error('Error fetching checklist:', err);
      setError('Failed to fetch checklist details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'text-green-600 bg-green-100';
      case 'IN_PROGRESS': return 'text-yellow-600 bg-yellow-100';
      case 'PENDING': return 'text-gray-600 bg-gray-100';
      case 'EXPIRED': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const calculateCompletionScore = () => {
    if (!checklist?.checklistItems) return 0;
    const completed = checklist.checklistItems.filter(item => item.checked).length;
    return Math.round((completed / checklist.checklistItems.length) * 100);
  };

  const handlePrintReport = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert('Please allow pop-ups to print the report');
      return;
    }

    const completionScore = calculateCompletionScore();
    const completedItems = checklist.checklistItems.filter(item => item.checked).length;
    const totalItems = checklist.checklistItems.length;
    const pendingItems = totalItems - completedItems;

    // Generate print-friendly HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Inspection Report - ${checklist.customerName || 'N/A'}</title>
          <style>
            @media print {
              @page {
                margin: 1cm;
                size: A4;
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              color: #333;
              padding: 20px;
              background: white;
            }
            
            .header {
              text-align: center;
              border-bottom: 3px solid #2563eb;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            
            .header h1 {
              color: #2563eb;
              font-size: 28px;
              margin-bottom: 5px;
            }
            
            .header p {
              color: #666;
              font-size: 14px;
            }
            
            .info-section {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 25px;
            }
            
            .info-section h2 {
              color: #1e40af;
              font-size: 18px;
              margin-bottom: 15px;
              border-bottom: 2px solid #3b82f6;
              padding-bottom: 8px;
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 15px;
            }
            
            .info-item {
              display: flex;
              flex-direction: column;
            }
            
            .info-label {
              font-weight: 600;
              color: #64748b;
              font-size: 12px;
              text-transform: uppercase;
              margin-bottom: 4px;
            }
            
            .info-value {
              color: #1e293b;
              font-size: 14px;
            }
            
            .summary-section {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 25px;
            }
            
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
              margin-top: 15px;
            }
            
            .summary-card {
              background: rgba(255, 255, 255, 0.2);
              border-radius: 6px;
              padding: 15px;
              text-align: center;
            }
            
            .summary-number {
              font-size: 32px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .summary-label {
              font-size: 12px;
              opacity: 0.9;
            }
            
            .checklist-section {
              margin-bottom: 25px;
            }
            
            .checklist-section h2 {
              color: #1e40af;
              font-size: 18px;
              margin-bottom: 15px;
              border-bottom: 2px solid #3b82f6;
              padding-bottom: 8px;
            }
            
            .checklist-item {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 15px;
              margin-bottom: 12px;
              page-break-inside: avoid;
            }
            
            .item-header {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              margin-bottom: 8px;
            }
            
            .item-status {
              flex-shrink: 0;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: bold;
              font-size: 14px;
            }
            
            .status-completed {
              background: #10b981;
              color: white;
            }
            
            .status-pending {
              background: #e2e8f0;
              color: #64748b;
            }
            
            .item-description {
              flex: 1;
              font-size: 14px;
              color: #1e293b;
              font-weight: 600;
            }
            
            .item-number {
              display: inline-block;
              background: #3b82f6;
              color: white;
              padding: 2px 8px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: bold;
              margin-right: 8px;
            }
            
            .item-notes {
              background: #fef3c7;
              border-left: 4px solid #f59e0b;
              padding: 12px;
              margin-top: 10px;
              font-size: 13px;
              color: #92400e;
              border-radius: 4px;
            }
            
            .item-photo {
              margin-top: 10px;
              background: #eff6ff;
              border-left: 4px solid #3b82f6;
              padding: 12px;
              border-radius: 4px;
            }
            
            .item-photo img {
              max-width: 100%;
              height: auto;
              max-height: 300px;
              border-radius: 6px;
              border: 2px solid #dbeafe;
              margin-top: 8px;
            }
            
            .notes-section {
              background: #f0fdf4;
              border: 1px solid #bbf7d0;
              border-radius: 8px;
              padding: 20px;
              margin-top: 25px;
              page-break-inside: avoid;
            }
            
            .notes-section h3 {
              color: #166534;
              font-size: 16px;
              margin-bottom: 10px;
            }
            
            .notes-content {
              color: #166534;
              font-size: 14px;
              white-space: pre-wrap;
              line-height: 1.8;
            }
            
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e2e8f0;
              text-align: center;
              font-size: 12px;
              color: #64748b;
            }
            
            .status-badge {
              display: inline-block;
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 600;
            }
            
            .badge-completed {
              background: #d1fae5;
              color: #065f46;
            }
            
            .badge-in-progress {
              background: #fef3c7;
              color: #92400e;
            }
            
            .badge-pending {
              background: #e2e8f0;
              color: #475569;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🏠 Property Inspection Report</h1>
            <p>Ace Star Tech - Property Maintenance Service</p>
            <p style="margin-top: 10px; font-size: 12px;">Report ID: ${checklistId}</p>
          </div>

          <div class="info-section">
            <h2>📋 Inspection Details</h2>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Customer Name</span>
                <span class="info-value">${checklist.customerName || 'Not specified'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Property Address</span>
                <span class="info-value">${checklist.propertyAddress || 'Not specified'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Inspection Date</span>
                <span class="info-value">${checklist.inspectionDate ? new Date(checklist.inspectionDate).toLocaleDateString('en-SG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Not scheduled'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Completed At</span>
                <span class="info-value">${checklist.completedAt ? new Date(checklist.completedAt).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' }) : 'Not completed'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Assigned To</span>
                <span class="info-value">${checklist.assignedTo || 'Not assigned'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Status</span>
                <span class="info-value">
                  <span class="status-badge badge-${(checklist.status || 'PENDING').toLowerCase().replace('_', '-')}">
                    ${checklist.status || 'PENDING'}
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div class="summary-section">
            <h2 style="color: white; border-bottom: 2px solid rgba(255,255,255,0.3); margin: 0;">Inspection Summary</h2>
            <div class="summary-grid">
              <div class="summary-card">
                <div class="summary-number">${completionScore}%</div>
                <div class="summary-label">Completion Rate</div>
              </div>
              <div class="summary-card">
                <div class="summary-number">${completedItems}/${totalItems}</div>
                <div class="summary-label">Items Completed</div>
              </div>
              <div class="summary-card">
                <div class="summary-number">${pendingItems}</div>
                <div class="summary-label">Items Pending</div>
              </div>
            </div>
          </div>

          <div class="checklist-section">
            <h2>✅ Checklist Items</h2>
            ${checklist.checklistItems.map((item, index) => `
              <div class="checklist-item">
                <div class="item-header">
                  <div class="item-status ${item.checked ? 'status-completed' : 'status-pending'}">
                    ${item.checked ? '✓' : '○'}
                  </div>
                  <div class="item-description">
                    <span class="item-number">#${index + 1}</span>
                    <strong>${item.description || `Inspection Item ${index + 1}`}</strong>
                  </div>
                </div>
                ${item.notes ? `
                  <div class="item-notes">
                    <strong>📝 Inspector Notes:</strong><br>
                    ${item.notes}
                  </div>
                ` : ''}
                ${item.photo ? `
                  <div class="item-photo">
                    <strong>📷 Evidence Photo:</strong><br>
                    <img src="${item.photo}" alt="Evidence for ${item.description || 'item ' + (index + 1)}" />
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>

          ${checklist.teamNotes ? `
            <div class="notes-section">
              <h3>💬 Team Notes & Comments</h3>
              <div class="notes-content">${checklist.teamNotes}</div>
            </div>
          ` : ''}

          <div class="footer">
            <p><strong>Ace Star Tech - Property Maintenance Service</strong></p>
            <p>Generated on ${new Date().toLocaleString('en-SG', { dateStyle: 'full', timeStyle: 'short' })}</p>
            <p style="margin-top: 10px;">This is an official inspection report. For any queries, please contact our support team.</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 250);
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
            <p className="mt-1 text-sm text-gray-500">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/admin/inspections')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <ArrowLeft className="-ml-1 mr-2 h-5 w-5" />
                Back to Inspections
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/inspections')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mb-4"
          >
            <ArrowLeft className="-ml-0.5 mr-2 h-4 w-4" />
            Back to Inspections
          </button>
          
          <div className="border-b border-gray-200 pb-5">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Inspection Checklist Details
            </h3>
            <p className="mt-2 max-w-4xl text-sm text-gray-500">
              Detailed view of the inspection checklist and completion status.
            </p>
          </div>
        </div>

        {checklist && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Checklist Items */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Checklist Items ({checklist.checklistItems?.length || 0} items)
                  </h3>
                  
                  {!checklist.checklistItems || checklist.checklistItems.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No checklist items found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {checklist.checklistItems.map((item, index) => (
                        <div 
                          key={item._id || index} 
                          className={`border-2 rounded-lg p-4 transition-all ${
                            item.checked 
                              ? 'border-green-200 bg-green-50' 
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            {/* Status Icon */}
                            <div className="flex-shrink-0 pt-1">
                              {item.checked ? (
                                <div className="bg-green-500 rounded-full p-1">
                                  <CheckCircle className="h-5 w-5 text-white" />
                                </div>
                              ) : (
                                <div className="bg-gray-300 rounded-full p-1">
                                  <XCircle className="h-5 w-5 text-white" />
                                </div>
                              )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* Item Number and Description */}
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1">
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold mb-1 ${
                                    item.checked ? 'bg-green-600 text-white' : 'bg-gray-500 text-white'
                                  }`}>
                                    Item #{index + 1}
                                  </span>
                                  <h4 className={`text-base font-semibold mt-1 ${
                                    item.checked ? 'text-green-900' : 'text-gray-900'
                                  }`}>
                                    {item.description || `Inspection Item ${index + 1}`}
                                  </h4>
                                </div>
                                
                                {/* Indicators */}
                                <div className="flex gap-2 flex-shrink-0">
                                  {item.notes && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                                      📝 Has Notes
                                    </span>
                                  )}
                                  {item.photo && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                      📷 Has Photo
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Notes Section */}
                              {item.notes && (
                                <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-r-md">
                                  <div className="flex">
                                    <div className="flex-shrink-0">
                                      <span className="text-lg">📝</span>
                                    </div>
                                    <div className="ml-3">
                                      <p className="text-sm font-semibold text-yellow-900 mb-1">Notes:</p>
                                      <p className="text-sm text-yellow-800 whitespace-pre-wrap">{item.notes}</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Photo Section */}
                              {item.photo && (
                                <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 p-3 rounded-r-md">
                                  <div className="flex flex-col">
                                    <div className="flex items-center mb-2">
                                      <span className="text-lg mr-2">📷</span>
                                      <p className="text-sm font-semibold text-blue-900">Uploaded Photo:</p>
                                    </div>
                                    <div className="mt-2">
                                      <img 
                                        src={item.photo} 
                                        alt={`${item.description || 'Inspection item'} - Evidence`} 
                                        className="max-w-full h-auto rounded-lg shadow-md border-2 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                                        onClick={() => window.open(item.photo, '_blank')}
                                        style={{ maxHeight: '400px', objectFit: 'contain' }}
                                      />
                                      <p className="text-xs text-blue-700 mt-1">Click to view full size</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Team Notes */}
              {checklist.teamNotes && (
                <div className="mt-8 bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Team Notes
                    </h3>
                    <div className="bg-gray-50 rounded-md p-4">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {checklist.teamNotes}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Summary Card */}
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Inspection Summary
                  </h3>
                  
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        Customer
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {checklist.customerName || 'Not specified'}
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500 flex items-center">
                        <MapPin className="h-4 w-4 mr-1" />
                        Property Address
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {checklist.propertyAddress || 'Not specified'}
                      </dd>
                    </div>

                    {checklist.inspectionDate && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          Inspection Date
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(checklist.inspectionDate).toLocaleDateString()}
                        </dd>
                      </div>
                    )}

                    {checklist.completedAt && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Completed At
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(checklist.completedAt).toLocaleString()}
                        </dd>
                      </div>
                    )}

                    <div>
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(checklist.status || 'PENDING')}`}>
                          {checklist.status || 'PENDING'}
                        </span>
                      </dd>
                    </div>

                    <div>
                      <dt className="text-sm font-medium text-gray-500">Completion Score</dt>
                      <dd className="mt-1">
                        <div className="flex items-center">
                          <span className="text-2xl font-bold text-blue-600">
                            {calculateCompletionScore()}%
                          </span>
                          <div className="ml-3 flex-1">
                            <div className="bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${calculateCompletionScore()}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </dd>
                    </div>

                    {checklist.assignedTo && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {checklist.assignedTo}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Actions
                  </h3>
                  
                  <div className="space-y-3">
                    <button
                      onClick={handlePrintReport}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Printer className="-ml-1 mr-2 h-5 w-5" />
                      Print Report
                    </button>
                    
                    <button
                      onClick={() => navigate('/admin/inspections')}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Back to Management
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Debug Section - Only in Development */}
        {process.env.NODE_ENV === 'development' && checklist && (
          <div className="mt-8 bg-gray-900 text-white p-4 rounded-lg">
            <h3 className="text-lg font-bold mb-2">Debug Info (Development Only)</h3>
            <pre className="text-xs overflow-auto">
              {JSON.stringify(checklist, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistDetailPage;